// src/pages/Builders/Dashboard.jsx
import React, { useEffect, useState } from "react";
import Layout from "../../layout/builders/Layout";
import CardTile from "../../layout/builders/CardTile";
import ProjectTiles from "../../layout/builders/Dashboard/ProjectTile";
import ProfileProgress from "../../layout/builders/ProfileDetails/ProfileProgress";
import Piechart from "../../layout/builders/Dashboard/Piechart";
import Barchart from "../../layout/builders/Dashboard/Barchart";
import calculateProfileCompletion from "../../layout/builders/ProfileDetails/calculateProfileCompletion";
import useProfileStore from "../../store/builders/useProfileStore";
import useDashboardStore from "../../store/builders/useDashboardStore";
import RankingDisplay from "../../layout/builders/Dashboard/RankingDisplay";
import useAuthStore from "../../store/useAuthStore";
import { axiosInstance } from "../../lib/axiosInstance";
import { AiOutlineLoading3Quarters } from "react-icons/ai"; // Re-added for consistency if needed elsewhere
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CreditCardIcon, // For 'approved' status
  LockClosedIcon, // For 'on_hold' if needed, using InformationCircleIcon for now
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom"; // Import useNavigate

const Dashboard = () => {
  const { profile, fetchProfile } = useProfileStore();
  const { getCardTile, getProjectTile, getChartData } = useDashboardStore();
  const { user, token } = useAuthStore.getState(); // Get user and token
  const navigate = useNavigate(); // Initialize useNavigate

  const [profileCompletion, setProfileCompletion] = useState(0);
  const [rankingData, setRankingData] = useState({
    state: { rank: 0, total: 0 },
    district: { rank: 0, total: 0 },
    taluk: { rank: 0, total: 0 },
  });

  const [builderStatus, setBuilderStatus] = useState(null);
  const [isStatusApiLoading, setIsStatusApiLoading] = useState(false); // Renamed to avoid conflict
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

  useEffect(() => {
    const loadInitialData = async () => {
      await fetchProfile(); // Fetches profile and sets it in store
      await getCardTile();
      await getProjectTile();
      await getChartData();
    };
    loadInitialData();
  }, [fetchProfile, getCardTile, getProjectTile, getChartData]);

  useEffect(() => {
    if (profile) {
      const completionPercentage = calculateProfileCompletion(profile);
      setProfileCompletion(completionPercentage);
    }
  }, [profile]); // Recalculate when profile from store changes

  useEffect(() => {
    const fetchBuilderAuthStatus = async () => {
      // Check if user and user.builderUniqueId exist, and token is present
      if (user && user.builderUniqueId && token) {
        setIsStatusApiLoading(true);
        try {
          const response = await axiosInstance.get(
            `/builders/auth/status/${user.builderUniqueId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          if (response.data && response.data.status) {
            const status = response.data.status;
            setBuilderStatus(status);
            // Determine if modal should be shown based on status
            if (
              ["new", "Pending_Verification", "Approved", "Rejected", "On_Hold"].includes(status)
            ) {
              setIsStatusModalOpen(true);
            }
          }
        } catch (error) {
          console.error("Error fetching builder status:", error);
          // Optionally, set an error state here to inform the user
        } finally {
          setIsStatusApiLoading(false);
        }
      } else {
        // Log if critical info for API call is missing
        if (!user) console.log("Dashboard: User object is null.");
        else if (!user.builderUniqueId) console.log("Dashboard: builderUniqueId is missing.");
        if (!token) console.log("Dashboard: Auth token is missing.");
      }
    };

    fetchBuilderAuthStatus();
  }, [user, token]); // Depend on user and token from the auth store

  const getModalContent = () => {
    switch (builderStatus) {
      case "new":
      case "Pending_Verification":
        return {
          icon: <ShieldCheckIcon className="w-16 h-16 text-yellow-500 mx-auto mb-5" />,
          title: "Verification Pending",
          message:
            "Your account is currently pending verification. You will receive an update shortly. Thank you for joining Ducktail!",
          button: null, // No button action for this status
        };
      case "Approved":
        return {
          icon: <CreditCardIcon className="w-16 h-16 text-green-500 mx-auto mb-5" />,
          title: "Account Verified!",
          message:
            "You are verified successfully! Please complete the payment to activate your account and access all features.",
          button: (
            <button
              onClick={() => navigate("/builder/subscription")}
              className="mt-6 px-6 py-2.5 bg-blue-500 text-white font-medium text-sm rounded-lg hover:bg-blue-600 transition-colors shadow-md hover:shadow-lg"
            >
              Go to Payment
            </button>
          ),
        };
      case "Rejected":
        return {
          icon: <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-5" />,
          title: "Application Rejected",
          message:
            "Your application has been rejected. Please check your email for further details and the next actionable steps.",
          button: (
            <button
              onClick={() => setIsStatusModalOpen(false)} // Or navigate to a support page
              className="mt-6 px-6 py-2.5 bg-gray-200 text-gray-700 font-medium text-sm rounded-lg hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          ),
        };
      case "On_Hold":
        return {
          icon: <InformationCircleIcon className="w-16 h-16 text-orange-500 mx-auto mb-5" />,
          title: "Account On Hold",
          message:
            "Your account is currently on hold. Please check your email or contact the admin for more information.",
          button: (
             <button
              onClick={() => setIsStatusModalOpen(false)} // Or navigate to a support page
              className="mt-6 px-6 py-2.5 bg-gray-200 text-gray-700 font-medium text-sm rounded-lg hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          ),
        };
      default:
        return null; // No modal for other statuses (e.g., fully active)
    }
  };

  const modalContent = getModalContent();
  const showBlurOverlay = isStatusModalOpen && modalContent;


  return (
    <div className="bg-gray-200 min-h-screen">
      <Layout>
        {/* Status Loader */}
        {isStatusApiLoading && (
          <div className="fixed inset-0 z-[101] flex items-center justify-center bg-white/50">
            <AiOutlineLoading3Quarters className="animate-spin text-3xl text-blue-600" />
             <p className="ml-3 text-gray-700">Checking account status...</p>
          </div>
        )}

        {/* Status Modal */}
        <AnimatePresence>
          {showBlurOverlay && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
              // onClick={() => { if (!modalContent.button) setIsStatusModalOpen(false); }} // Close on overlay click only if no button
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.8, opacity: 0, y: 20 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 max-w-lg w-full text-center"
                onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
              >
                {modalContent.icon}
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-3">
                  {modalContent.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-6 leading-relaxed">
                  {modalContent.message}
                </p>
                {modalContent.button}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Dashboard Content */}
        <div className={showBlurOverlay ? "blur-sm pointer-events-none" : ""}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-5">
            <div className="w-full md:w-auto flex justify-center md:justify-start">
              <CardTile />
            </div>
            <div className="w-full md:w-auto flex justify-center">
              <RankingDisplay rankingData={rankingData} />
            </div>
            <div className="w-full md:w-auto flex justify-center md:justify-end">
              <ProfileProgress progress={profileCompletion} />
            </div>
          </div>

          <div className="mt-5 py-5">
            <ProjectTiles />
          </div>

          <div className="flex flex-col lg:flex-row justify-between mt-4 gap-4">
            <div className="w-full lg:w-2/3">
              <Barchart />
            </div>
            <div className="w-full lg:w-1/3">
              <Piechart />
            </div>
          </div>
        </div>
      </Layout>
    </div>
  );
};

export default Dashboard;